import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, MapPin } from 'lucide-react'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { cn } from '@/lib/utils'

interface Props {
  value?: string
  onChange: (value: string | undefined) => void
  cities: string[]
  className?: string
}

// City filter (Figma 1140:41675): select-field trigger with a searchable panel.
// Pick a known city from the list or type free text and press Enter / "Use …".
export function TechnicianCityFilter({
  value,
  onChange,
  cities,
  className,
}: Props) {
  const { t } = useTranslation('technicians')
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState(value ?? '')
  const ref = useRef<HTMLDivElement>(null)
  const openRef = useRef(open)

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!openRef.current) return
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (!openRef.current) return
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const trimmedTerm = term.trim()

  const filtered = useMemo(() => {
    const q = trimmedTerm.toLowerCase()
    if (!q) return cities
    return cities.filter(c => c.toLowerCase().includes(q))
  }, [cities, trimmedTerm])

  const apply = (next: string) => {
    const trimmed = next.trim()
    onChange(trimmed || undefined)
    setOpen(false)
  }

  const triggerLabel = value ?? t('list.filters.cityPlaceholder')
  const showCustomApply =
    trimmedTerm.length > 0 &&
    !filtered.some(c => c.toLowerCase() === trimmedTerm.toLowerCase())

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type='button'
        aria-expanded={open}
        onClick={() => {
          if (!open) setTerm(value ?? '')
          setOpen(o => !o)
        }}
        className={cn(
          'inline-flex h-10 min-w-[132px] shrink-0 items-center justify-between gap-2 rounded-xl border border-[#e7e9ef] bg-white py-0 pl-4 pr-3 text-sm font-normal leading-none text-[#171a20]',
          FORM_CONTROL_FOCUS_CLASS,
          FORM_FILTER_HOVER_CLASS,
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='flex min-w-0 items-center gap-2'>
          <MapPin
            className='size-4 shrink-0 text-[#272c36]'
            strokeWidth={1.9}
          />
          <span className='truncate'>{triggerLabel}</span>
        </span>
        <ChevronDown className='size-4 shrink-0 text-[#272c36]' />
      </button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 w-[240px] rounded-xl border border-[#e7e9ef] bg-white p-2 text-popover-foreground shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]'>
          <input
            autoFocus
            value={term}
            onChange={e => setTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                apply(term)
              }
            }}
            placeholder={t('list.filters.citySearchPlaceholder')}
            className='mb-2 h-9 w-full rounded-lg border border-[#e7e9ef] bg-transparent px-3 text-sm outline-none focus-visible:ring-0'
          />

          {value && (
            <button
              type='button'
              onClick={() => apply('')}
              className='mb-1 w-full rounded px-2 py-1 text-left text-xs text-primary hover:bg-accent'
            >
              {t('list.filters.clearCity')}
            </button>
          )}

          <div className='max-h-48 overflow-y-auto'>
            {showCustomApply && (
              <button
                type='button'
                onClick={() => apply(term)}
                className='w-full rounded px-2 py-2 text-left text-sm hover:bg-accent'
              >
                {t('list.filters.useCitySearch', { city: trimmedTerm })}
              </button>
            )}

            {filtered.map(city => (
              <button
                key={city}
                type='button'
                onClick={() => apply(city)}
                className={cn(
                  'w-full rounded px-2 py-2 text-left text-sm font-normal hover:bg-accent',
                  value === city && 'bg-accent'
                )}
              >
                {city}
              </button>
            ))}

            {filtered.length === 0 && !showCustomApply && (
              <div className='px-2 py-2 text-sm text-muted-foreground'>
                {t('list.filters.noCityOptions')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
